import { DateField } from '@cmsgov/ds-medicare-gov';
import { useState, useEffect, ComponentProps, useCallback } from 'react';
import '../css/date.field.css';
import addLeadingZeroes from '../functions/addLeadingZeroes';
import capitalizeFirstLetter from '../functions/capitalizeFirstLetter';
import { isValidYear } from '../functions/isValidYear';
import translate from '../functions/translate';
import { DateObject } from '../objects/models/date.field.value';

type DateFieldProps = ComponentProps<typeof DateField>;
type DateFieldsProps = Pick<DateFieldProps, 'label' | 'hint' | 'dayFieldRef' | 'monthFieldRef' | 'yearFieldRef'> & {
    date: DateObject
    onBlur: (newVal: DateObject) => void
    onChange: (newVal: DateObject) => void
    forceValidation: boolean
    hideDayField?: boolean
    errorMessage: string
    apiToFieldValidation?: boolean
    testId?: string
}

const DateFields = (props: DateFieldsProps) => {
    const [dayRefField, setDayRefField] = useState<HTMLInputElement>();
    const [fieldsHaveBeenVisited, setFieldsHaveBeenVisited] = useState(false);
    const [isDateInvalid, setIsDateInvalid] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const eventTypes = ['onBlur', 'onChange'] as const;
    type DateEventType = typeof eventTypes[number];
    const fieldNames = ['day', 'month', 'year'] as const;
    type DateFieldName = typeof fieldNames[number];

    const allFieldsAreValid = useCallback(() => {
        return props.date.day !== '' && props.date.month !== '' && props.date.year.length > 3 && isValidYear(props.date.year);
    }, [props.date.day, props.date.month, props.date.year]);

    const allVisitedFieldsAreValid = useCallback(() => {
        return (props.date.day !== '' || !fieldsHaveBeenVisited) && (props.date.month !== '' || !fieldsHaveBeenVisited) && (isValidYear(props.date.year) || !fieldsHaveBeenVisited);
    }, [props.date.day, props.date.month, props.date.year, fieldsHaveBeenVisited]);

    const fieldsFilled = useCallback(() => {
        return props.date.day !== '' && props.date.month !== '' && props.date.year.length > 3;
    }, [props.date.day, props.date.month, props.date.year]);

    const fieldsAreValid = useCallback(() => {
        return props.date.month !== '' && props.date.year.length > 3 && isValidYear(props.date.year);
    }, [props.date.month, props.date.year]);

    const visitedFieldsAreValid = useCallback(() => {
        return  (props.date.month !== '' || !fieldsHaveBeenVisited) && (isValidYear(props.date.year) || !fieldsHaveBeenVisited);
    }, [props.date.month, props.date.year, fieldsHaveBeenVisited]);

    const fieldsFilledWithoutDay = useCallback(() => {
        return props.date.month !== '' && props.date.year.length > 3;
    }, [props.date.month, props.date.year]);

    // hide day field
    useEffect(() => {
        if (props.hideDayField && dayRefField) {
            const parentDiv = dayRefField.closest('div');
            const separatorEl = parentDiv?.previousSibling as HTMLElement;

            [parentDiv, separatorEl].forEach(el => {
                if (el) {
                    el.setAttribute('style', 'display: none');
                }
            })
        }
    }, [dayRefField, props.date, props.hideDayField]);

    useEffect(() => {
        if (!props.hideDayField) {
            setDateErrorMessage();
            resetDateErrorMessage();
        }

        function setDateErrorMessage() {
            if ((fieldsFilled() && !isValidYear(props.date.year)) || props.forceValidation) {
                setErrorMessage(props.errorMessage);
                setIsDateInvalid(true);
            }
        }
        function resetDateErrorMessage() {
            if ((!props.forceValidation && allVisitedFieldsAreValid()) || (props.forceValidation && allFieldsAreValid())) {
                setErrorMessage(null);
                setIsDateInvalid(false);
            }
        }
    }, [allFieldsAreValid, allVisitedFieldsAreValid, props.forceValidation, fieldsFilled, props.date.year, props.errorMessage, props.hideDayField]);

    // applies to create account and unlock account, YourMedicareInformation, which don't have a day field
    useEffect(() => {
        if (props.hideDayField) {
            setNoDayDateErrorMessage();
            setErrorFromApi();
            resetErrors();
        }

        function setNoDayDateErrorMessage() {
            if ((fieldsFilledWithoutDay() && !isValidYear(props.date.year)) || props.forceValidation) {
                setErrorMessage(props.errorMessage);
                setIsDateInvalid(true);
            }
        }
        function setErrorFromApi() {
            if (props.apiToFieldValidation === true) {
                setErrorMessage(translate('check_medicare_cover_start_date'));
                setIsDateInvalid(true);
            }
        }
        function resetErrors() {
            if (!props.apiToFieldValidation && ((!props.forceValidation && visitedFieldsAreValid()) || (props.forceValidation && fieldsAreValid()))) {
                setErrorMessage(null);
                setIsDateInvalid(false);
            }
        }
    }, [props.hideDayField, fieldsAreValid, props.apiToFieldValidation, props.date.year, props.errorMessage, props.forceValidation, visitedFieldsAreValid, fieldsFilledWithoutDay]);

    const createUpdatedDateObject = (dateObject: DateObject) => {
        dateObject.day = dateObject.day.replace(/\D/g, '');
        dateObject.month = dateObject.month.replace(/\D/g, '');
        dateObject.year = dateObject.year.replace(/\D/g, '');
        if (dateObject.day !== '') {
            const day = parseInt(dateObject.day, 10) || 0;
            if (isNaN(day) || day < 0 || day > 31)
                dateObject.day = '01';
        }
        if (dateObject.month !== '') {
            const month = parseInt(dateObject.month, 10) || 0;
            if (isNaN(month) || month < 0 || month > 12)
                dateObject.month = '01';
        }
    };

    const formatValues = (dateObject: DateObject, fieldName: DateFieldName) => {
        if (fieldName === 'day' && dateObject.day !== '' && !props.hideDayField)
            dateObject.day = addLeadingZeroes(parseInt(dateObject.day, 10), 2);
        if (fieldName === 'month' && dateObject.month !== '')
            dateObject.month = addLeadingZeroes(parseInt(dateObject.month, 10), 2);
    };

    const invokeEventHandlersFromCaller = (updatedDateObject: DateObject, eventType: DateEventType) => {
        if (eventType === 'onBlur' && props.onBlur !== null)
            props.onBlur(updatedDateObject);
        if (eventType === 'onChange' && props.onChange !== null)
            props.onChange(updatedDateObject);
    };

    const updateDate = (dateObject: DateObject, eventType: DateEventType, fieldName: DateFieldName) => {
        createUpdatedDateObject(dateObject);
        if (eventType === 'onBlur') {
            formatValues(dateObject, fieldName);
        }
        invokeEventHandlersFromCaller(dateObject, eventType);
        updateValidationTrackingVariables(dateObject);
    };

    const updateValidationTrackingVariables = (dateObject: DateObject) => {
        if ((props.hideDayField || dateObject.day !== '') && dateObject.month !== '' && dateObject.year !== '') {
            setFieldsHaveBeenVisited(true);
        }
    };

    return <>
        <DateField
            autoComplete={true}
            hint={props.hint}
            label={props.hideDayField ? "" : props.label}
            dayFieldRef={props.hideDayField ? setDayRefField : props.dayFieldRef}
            data-testid={props.testId}
            dayInvalid={isDateInvalid}
            dayLabel={capitalizeFirstLetter(translate('day'))}
            dayValue={props.hideDayField ? "01" : props.date.day}
            monthFieldRef={props.monthFieldRef}
            monthInvalid={isDateInvalid}
            monthLabel={capitalizeFirstLetter(translate('month'))}
            monthValue={props.date.month}
            yearFieldRef={props.yearFieldRef}
            yearInvalid={isDateInvalid}
            yearLabel={capitalizeFirstLetter(translate('year'))}
            yearValue={props.date.year}
            onBlur={(event, dateObject) => updateDate(dateObject, 'onBlur', event.currentTarget.name)}
            onChange={(event, dateObject) => updateDate(dateObject, 'onChange', event.currentTarget.name)}
            errorMessage={errorMessage}
        />
    </>
}

export default DateFields;
